// Underlined tabs in the style of Isomorphic's page tabs (a muted rule, the active tab in the
// brand colour). The caller owns the selection, so it can live in the URL.
import type { ReactNode } from "react";

import { cn } from "./cn";

export interface TabItem<K extends string> {
  key: K;
  label: ReactNode;
  /** A small count or mark after the label. */
  badge?: ReactNode;
}

export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: {
  tabs: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  label: string;
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className={cn("custom-scrollbar flex gap-6 overflow-x-auto border-b border-muted", className)}>
      {tabs.map((t) => {
        const on = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`tab-${t.key}`}
            aria-selected={on}
            aria-controls={`panel-${t.key}`}
            onClick={() => onChange(t.key)}
            className={cn(
              "-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-0.5 pb-3 pt-1 text-sm font-semibold transition-colors",
              on ? "border-primary text-gray-1000" : "border-transparent text-gray-500 hover:text-gray-900",
            )}
          >
            {t.label}
            {t.badge}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, children, className }: { id: string; children: ReactNode; className?: string }) {
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className={className}>
      {children}
    </div>
  );
}
